import React from 'react'
import PropTypes from 'prop-types'
import { Card, Spin, Alert, Layout, Button } from 'antd'
import { GoogleMap, Marker, InfoWindow, Circle } from '@react-google-maps/api'
import { PlusOutlined } from '@ant-design/icons'
import ProjectList from './ProjectList'
import styles from './styles.css'

const { Sider, Content } = Layout

const mapContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 250px)',
  minHeight: '600px'
}

const defaultCenter = {
  lat: 54.5, // Center of UK
  lng: -2.5
}

class MapView extends React.PureComponent {

  static propTypes = {
    mapData: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    googleMapsApiKey: PropTypes.string.isRequired,
    fetchMapData: PropTypes.func.isRequired,
    fetchNearbySubcontractors: PropTypes.func.isRequired,
    openProjectCellModal: PropTypes.func.isRequired,
    controls: PropTypes.object.isRequired,
    additionalFilter: PropTypes.object,
  }

  static defaultProps = {
    error: null,
    additionalFilter: {},
  }

  state = {
    selectedMarker: null,
    selectedProjectId: null,
    mapCenter: defaultCenter,
    mapZoom: 6,
    hasFetchedData: false,
    isLoaded: false,
    searchRadius: 5,
    nearbySubcontractors: [],
    showNearbySubcontractors: false,
    loadingNearby: false,
    searchMode: false,
  }

  componentDidMount() {
    if (!this.state.hasFetchedData) {
      this.setState({ hasFetchedData: true })
      this.props.fetchMapData()
    }

    if (window.google && window.google.maps) {
      this.setState({ isLoaded: true })
    } else {
      this.loadGoogleMapsScript()
    }
  }

  loadGoogleMapsScript = () => {
    const { googleMapsApiKey } = this.props

    if (document.getElementById('google-maps-script')) {
      return
    }

    const script = document.createElement('script')
    script.id = 'google-maps-script'
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + (googleMapsApiKey || '') + '&libraries=places'
    script.async = true
    script.defer = true
    script.onload = () => {
      this.setState({ isLoaded: true })
      this.calculateMapBounds()
    }
    script.onerror = () => {
      console.error('Failed to load Google Maps script')
    }
    document.head.appendChild(script)
  }

  onLoad = () => {
    this.setState({ isLoaded: true })
    this.calculateMapBounds()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.mapData !== this.props.mapData) {
      // If a project is selected, keep the map centered/zoomed where the user left it.
      // We only need the new marker data to re-render.
      if (!this.state.selectedProjectId) {
        this.calculateMapBounds()

        // Auto-select project from additionalFilter (componentView mode)
        var additionalFilter = this.props.additionalFilter || {}
        if (additionalFilter.project_id) {
          var projects = (this.props.mapData && this.props.mapData.projects) || []
          var project = projects.find(function(p) { return p.id === additionalFilter.project_id })
          if (project) {
            this.handleSelectProject(project.id)
          }
        }
      } else {
        const selectedProjectId = this.state.selectedProjectId
        const projects = (this.props.mapData && this.props.mapData.projects) || []
        const selectedProject = projects.find(p => p.id === selectedProjectId)

        if (selectedProject && this.state.selectedMarker && this.state.selectedMarker.type === 'project') {
          // Update the selected marker reference so InfoWindow uses the latest data
          this.setState({ selectedMarker: selectedProject })
        }
      }
    }

    // Refetch data when filters change
    const prevControls = prevProps.controls
    const currentControls = this.props.controls

    if (prevControls && currentControls) {
      const filtersChanged = (
        prevControls.periodIndex !== currentControls.periodIndex ||
        prevControls.dayOfWeek !== currentControls.dayOfWeek ||
        prevControls.projectSearch !== currentControls.projectSearch ||
        JSON.stringify(prevControls.statusKey) !== JSON.stringify(currentControls.statusKey) ||
        JSON.stringify(prevControls.phaseKey) !== JSON.stringify(currentControls.phaseKey) ||
        JSON.stringify(prevControls.contractsManagerId) !== JSON.stringify(currentControls.contractsManagerId) ||
        prevControls.omitted !== currentControls.omitted
      )

      if (filtersChanged) {
        this.props.fetchMapData()
      }
    }
  }

  componentWillUnmount() {
    this.setState = () => {}
  }

  calculateMapBounds = () => {
    const { mapData } = this.props
    const activeSubcontractors = (mapData.subcontractors || []).filter(sub => !sub.deactivated_at)
    const allMarkers = [...(mapData.projects || []), ...activeSubcontractors]

    if (allMarkers.length === 0) return
    if (!window.google || !window.google.maps) return

    const bounds = new window.google.maps.LatLngBounds()
    allMarkers.forEach(marker => {
      if (marker.lat && marker.lng) {
        bounds.extend({ lat: marker.lat, lng: marker.lng })
      }
    })

    const center = bounds.getCenter()
    this.setState({
      mapCenter: { lat: center.lat(), lng: center.lng() }
    })
  }

  clearNearbySearchResults = () => {
    this.setState({
      nearbySubcontractors: [],
      showNearbySubcontractors: false,
      searchMode: false,
      loadingNearby: false,
    })
  }

  handleMarkerClick = (marker) => {
    // Only update selectedProjectId if clicking on a project marker
    // For subcontractor markers, keep the current selectedProjectId to preserve the search circle
    if (marker.type === 'project') {
      const shouldClearNearby = this.state.selectedProjectId !== marker.id

      this.setState({
        selectedMarker: marker,
        selectedProjectId: marker.id
      })

      if (shouldClearNearby) {
        this.clearNearbySearchResults()
      }
    } else {
      // Just update the selected marker for info window, don't clear selectedProjectId
      this.setState({
        selectedMarker: marker
      })
    }
  }

  handleSelectProject = (projectId) => {
    const { mapData } = this.props
    const project = (mapData.projects || []).find(p => p.id === projectId)

    const shouldClearNearby = this.state.selectedProjectId !== projectId

    if (project && project.lat && project.lng) {
      this.setState({
        selectedProjectId: projectId,
        selectedMarker: project,
        mapCenter: { lat: project.lat, lng: project.lng },
        mapZoom: 13
      })
    } else {
      this.setState({
        selectedProjectId: projectId,
        selectedMarker: null
      })
    }

    if (shouldClearNearby) {
      this.clearNearbySearchResults()
    }
  }

  handleOmit = (projectId) => {
    console.log('Omit project:', projectId)
  }

  handleUnomit = (projectId) => {
    console.log('Unomit project:', projectId)
  }

  calculateZoomForRadius = (radiusKm) => {
    // Calculate zoom level to fit circle in viewport
    // Google Maps zoom levels: each level doubles the scale
    // Approximate formula: zoom = log2(40075 / (radiusKm * 2.5))
    // where 40075 is Earth's circumference in km
    const zoom = Math.round(Math.log2(40075 / (radiusKm * 2.5)))
    return Math.max(6, Math.min(15, zoom)) // Clamp between 6 and 15
  }

  handleRadiusChange = (radius) => {
    const { selectedProjectId } = this.state
    const { mapData } = this.props

    this.setState({ searchRadius: radius })

    // If a project is selected and we're in search mode, adjust zoom
    if (selectedProjectId) {
      const projects = mapData.projects || []
      const selectedProject = projects.find(p => p.id === selectedProjectId)

      if (selectedProject && selectedProject.lat && selectedProject.lng) {
        const newZoom = this.calculateZoomForRadius(radius)
        this.setState({
          mapZoom: newZoom,
          mapCenter: { lat: selectedProject.lat, lng: selectedProject.lng }
        })
      }
    }
  }

  handleSearchSubcontractors = async (projectId) => {
    const { searchRadius } = this.state
    const { fetchNearbySubcontractors } = this.props

    this.setState({ loadingNearby: true, searchMode: true })

    try {
      const result = await fetchNearbySubcontractors(projectId, searchRadius)

      const activeSubcontractors = (result.subcontractors || []).filter(sub => !sub.deactivated_at)
      this.setState({
        nearbySubcontractors: activeSubcontractors,
        showNearbySubcontractors: true,
        loadingNearby: false
      })
    } catch (error) {
      this.setState({ loadingNearby: false })
    }
  }

  handleClearSelection = () => {
    this.setState({
      selectedProjectId: null,
      selectedMarker: null,
      nearbySubcontractors: [],
      showNearbySubcontractors: false,
      searchMode: false,
      mapZoom: 10
    })
    this.calculateMapBounds()
  }

  handleAddSubcontractorToProject = (subcontractor) => {
    const { selectedProjectId } = this.state
    const { mapData, controls, openProjectCellModal } = this.props

    console.log('handleAddSubcontractorToProject called with subcontractor:', subcontractor)

    if (!selectedProjectId) return

    const projects = mapData.projects || []
    const project = projects.find(p => p.id === selectedProjectId)

    if (!project) return

    // Calculate date from current planner controls
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const periodIndex = controls.periodIndex || 0
    const dayOfWeek = controls.dayOfWeek || 'Mon'
    const dayIndex = daysOfWeek.indexOf(dayOfWeek)

    // Calculate the date
    const now = new Date()
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay() + 1) // Monday of current week
    startOfWeek.setDate(startOfWeek.getDate() + (periodIndex * 7) + dayIndex)

    const year = startOfWeek.getFullYear()
    const month = String(startOfWeek.getMonth() + 1).padStart(2, '0')
    const day = String(startOfWeek.getDate()).padStart(2, '0')
    const dateStr = year + '-' + month + '-' + day

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const dateFormatted = startOfWeek.getDate() + ' ' + monthNames[startOfWeek.getMonth()] + ' ' + year

    const modalPayload = {
      period: {
        date: dateStr,
        date_formatted: dateFormatted,
        day_of_week: dayOfWeek,
      },
      project: {
        id: project.id,
        number: project.reference || ('P' + project.id),
        client_site: {
          client: { name: project.client_name || (project.clientSite && project.clientSite.client && project.clientSite.client.name) || '' },
          name: project.client_site_name || (project.clientSite && project.clientSite.name) || '',
        }
      },
      jobs: [],
      plannerCell: null,
      preselectedSubcontractorId: subcontractor.id,
    }

    console.log('Opening modal with payload:', modalPayload)
    openProjectCellModal(modalPayload)
  }

  handleInfoWindowClose = () => {
    this.setState({ selectedMarker: null })
  }

  createCustomMarkerIcon = (letter, color) => {
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="' + color + '" stroke="white" stroke-width="1.5"/><text x="12" y="16" font-size="12" font-weight="bold" fill="white" text-anchor="middle">' + letter + '</text></svg>'
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg)
  }

  renderProjectMarker = (project) => {
    return (
      <Marker
        key={`project-${project.id}`}
        position={{ lat: project.lat, lng: project.lng }}
        icon={this.createCustomMarkerIcon('P', '#4285f4')}
        onClick={() => this.handleMarkerClick(project)}
      />
    )
  }

  renderSubcontractorMarker = (subcontractor) => {
    const { mapData } = this.props
    const { selectedProjectId } = this.state

    let isBookedOnSelectedProject = false

    if (selectedProjectId) {
      const projects = mapData.projects || []
      const selectedProject = projects.find(p => p.id === selectedProjectId)
      const scheduled = (selectedProject && selectedProject.scheduled_subcontractors) || []

      isBookedOnSelectedProject = !!scheduled.find(sub => sub && sub.id === subcontractor.id)
    }

    const color = isBookedOnSelectedProject ? '#34a853' : '#ea4335'

    return (
      <Marker
        key={`subcontractor-${subcontractor.id}`}
        position={{ lat: subcontractor.lat, lng: subcontractor.lng }}
        icon={this.createCustomMarkerIcon('S', color)}
        onClick={() => this.handleMarkerClick(subcontractor)}
      />
    )
  }

  renderInfoWindow = () => {
    const { selectedMarker, nearbySubcontractors } = this.state
    const { mapData } = this.props

    if (!selectedMarker) return null

    const isProject = selectedMarker.type === 'project'

    // ALWAYS get fresh data from mapData by ID - never use selectedMarker data directly
    // This prevents any stale/accumulated data issues
    let displayMarker = null
    if (isProject) {
      const freshProject = (mapData.projects || []).find(p => p.id === selectedMarker.id)
      if (freshProject) {
        // Only use position from selectedMarker, all other data from fresh source
        displayMarker = {
          ...freshProject,
          scheduled_subcontractors: [...(freshProject.scheduled_subcontractors || [])]
        }
      }
    } else {
      // Check both mapData.subcontractors and nearbySubcontractors (search results)
      let freshSubcontractor = (mapData.subcontractors || []).find(s => s.id === selectedMarker.id)
      if (!freshSubcontractor) {
        freshSubcontractor = (nearbySubcontractors || []).find(s => s.id === selectedMarker.id)
      }
      if (freshSubcontractor) {
        displayMarker = {
          ...freshSubcontractor,
          scheduled_projects: [...(freshSubcontractor.scheduled_projects || [])],
          // Preserve distance info from nearby search which isn't in mapData
          distance_km: selectedMarker.distance_km,
          distance_miles: selectedMarker.distance_miles
        }
      }
    }

    // If we couldn't find fresh data, don't show the InfoWindow
    if (!displayMarker) return null

    return (
      <InfoWindow
        key={`infowindow-${displayMarker.type}-${displayMarker.id}`}
        position={{ lat: displayMarker.lat, lng: displayMarker.lng }}
        onCloseClick={this.handleInfoWindowClose}
      >
        <div className={styles.infoWindow}>
          {isProject ? (
            <div>
              <h3>
                <a href={'/app/teams/' + (window.location.pathname.split('/')[3] || 'vividfinishes') + '/projects/' + displayMarker.id + '/view'} className={styles.infoWindowLink}>
                  {displayMarker.title}
                </a>
              </h3>
              <p><strong>Client:</strong> {displayMarker.client_name}</p>
              <p><strong>Site:</strong> {displayMarker.site_name}</p>
              <p><strong>Address:</strong> {displayMarker.address}</p>
              {displayMarker.scheduled_subcontractors && displayMarker.scheduled_subcontractors.length > 0 && (
                <div>
                  <p><strong>Scheduled Subcontractors:</strong></p>
                  <ul>
                    {displayMarker.scheduled_subcontractors.map((sub, index) => (
                      <li key={sub.id + '-' + sub.date + '-' + index}>{sub.name} - {sub.date}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3>
                <a href={'/app/teams/' + (window.location.pathname.split('/')[3] || 'vividfinishes') + '/subcontractors/' + displayMarker.id + '/view'} className={styles.infoWindowLink}>
                  {displayMarker.name}
                </a>
              </h3>
              <p><strong>Type:</strong> {displayMarker.subcontractor_type}</p>
              {displayMarker.distance_km && (
                <p><strong>Distance:</strong> {displayMarker.distance_km} km ({displayMarker.distance_miles} miles)</p>
              )}
              <p><strong>Address:</strong> {displayMarker.address}</p>
              <p><strong>Phone:</strong> {displayMarker.telephone}</p>
              {displayMarker.scheduled_projects && displayMarker.scheduled_projects.length > 0 && (
                <div>
                  <p><strong>Scheduled Projects:</strong></p>
                  <ul>
                    {displayMarker.scheduled_projects.map(proj => (
                      <li key={proj.id}>{proj.title} - {proj.date}</li>
                    ))}
                  </ul>
                </div>
              )}
              {this.state.selectedProjectId && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block
                    onClick={() => this.handleAddSubcontractorToProject(displayMarker)}
                  >
                    Add to Project
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </InfoWindow>
    )
  }

  render() {
    const { mapData, loading, error, googleMapsApiKey } = this.props
    const {
      mapCenter,
      mapZoom,
      selectedProjectId,
      searchRadius,
      nearbySubcontractors,
      showNearbySubcontractors,
      loadingNearby,
      searchMode
    } = this.state

    if (loading) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" tip="Loading map data..." />
          </div>
        </Card>
      )
    }

    if (error) {
      return (
        <Card>
          <Alert
            message="Error Loading Map"
            description={error}
            type="error"
            showIcon
          />
        </Card>
      )
    }

    // Deduplicate projects by ID to prevent duplicate key warnings
    const seenProjectIds = new Set()
    const projects = (mapData.projects || []).filter(p => {
      if (seenProjectIds.has(p.id)) return false
      seenProjectIds.add(p.id)
      return true
    })
    const subcontractors = (mapData.subcontractors || []).filter(sub => !sub.deactivated_at)
    const { isLoaded } = this.state

    const selectedProject = selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null
    const displayProjects = projects // Always show all project markers
    const displaySubcontractors = showNearbySubcontractors ? nearbySubcontractors : subcontractors

    // Count distinct subcontractors booked on selected project (not total jobs)
    const bookedSubcontractorIds = selectedProject && selectedProject.scheduled_subcontractors
      ? [...new Set(selectedProject.scheduled_subcontractors.map(sub => sub.id))]
      : []
    const bookedCount = bookedSubcontractorIds.length

    return (
      <Card className={styles.mapCard}>
        <Layout style={{ background: 'white' }}>
          <Sider
            width={350}
            style={{
              background: '#fafafa',
              borderRight: '1px solid #e8e8e8',
              height: 'calc(100vh - 200px)',
              minHeight: '600px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #e8e8e8', background: 'white', flexShrink: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Projects ({projects.length})</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, height: 'calc(100% - 48px)' }}>
            <ProjectList
              projects={projects}
              selectedProjectId={selectedProjectId}
              onSelectProject={this.handleSelectProject}
              onOmit={this.handleOmit}
              onUnomit={this.handleUnomit}
              onSearchSubcontractors={this.handleSearchSubcontractors}
              searchRadius={searchRadius}
              onRadiusChange={this.handleRadiusChange}
              searchLoading={loadingNearby}
              searchResultCount={showNearbySubcontractors ? nearbySubcontractors.length : null}
            />
            </div>
          </Sider>

          <Content style={{ position: 'relative' }}>
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <span className={styles.blueDot}></span>
                <span>Projects ({displayProjects.length})</span>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.redDot}></span>
                <span>Subcontractors ({displaySubcontractors.length})</span>
              </div>
              {selectedProjectId && (
                <div className={styles.legendItem}>
                  <span className={styles.greenDot}></span>
                  <span>Booked on Selected Project ({bookedCount})</span>
                </div>
              )}
            </div>

            {isLoaded && window.google && window.google.maps ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {displayProjects.map(project => this.renderProjectMarker(project))}
                {displaySubcontractors.map(subcontractor => this.renderSubcontractorMarker(subcontractor))}
                {this.renderInfoWindow()}

                {selectedProject && selectedProject.lat && selectedProject.lng && (
                  <Circle
                    center={{ lat: selectedProject.lat, lng: selectedProject.lng }}
                    radius={searchRadius * 1000}
                    options={{
                      fillColor: '#1890ff',
                      fillOpacity: 0.1,
                      strokeColor: '#1890ff',
                      strokeOpacity: 0.5,
                      strokeWeight: 2,
                    }}
                  />
                )}
              </GoogleMap>
            ) : (
              <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip="Loading Google Maps..." />
              </div>
            )}
          </Content>
        </Layout>
      </Card>
    )
  }
}

export default MapView
